import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, ArrowRight, Trash2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { CartItemCard } from '../components/DishCard';

export default function CartPage() {
  const { cart, loading, clearCart, itemCount, fetchCart } = useCart();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [deliveryMode, setDeliveryMode] = React.useState(
    localStorage.getItem('delivery_mode') || 'livraison'
  );

  const selectMode = (mode) => {
    setDeliveryMode(mode);
    localStorage.setItem('delivery_mode', mode);
  };

  const freeDelivery = (cart.total || 0) >= 80;
  const grandTotal = (cart.total || 0) + (deliveryMode === 'livraison' && !freeDelivery ? 4 : 0);

  // Refresh cart when page loads
  React.useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated) {
    return (
      <main className="min-h-screen bg-transparent pt-24 pb-16">
        <div className="container-padding max-w-2xl mx-auto text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="w-20 h-20 mx-auto bg-terracotta/10 rounded-full flex items-center justify-center">
              <ShoppingBag className="text-terracotta" size={36} />
            </div>
            <h1 className="font-heading text-3xl font-bold text-navy">
              Connectez-vous pour voir votre panier
            </h1>
            <p className="text-navy/60">
              Créez un compte ou connectez-vous pour commencer à commander.
            </p>
            <Link to="/login" className="btn-primary inline-flex items-center gap-2">
              Se connecter
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-transparent pt-24 pb-16">
        <div className="container-padding max-w-4xl mx-auto py-12">
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white/50 h-24 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (itemCount === 0) {
    return (
      <main className="min-h-screen bg-transparent pt-24 pb-16">
        <div className="container-padding max-w-2xl mx-auto text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="w-20 h-20 mx-auto bg-terracotta/10 rounded-full flex items-center justify-center">
              <ShoppingBag className="text-terracotta" size={36} />
            </div>
            <h1 className="font-heading text-3xl font-bold text-navy">
              Votre panier est vide
            </h1>
            <p className="text-navy/60">
              Découvrez notre carte et ajoutez vos plats préférés.
            </p>
            <Link to="/menu" className="btn-primary inline-flex items-center gap-2">
              Voir la carte
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-transparent pt-24 pb-16">
      <div className="container-padding max-w-4xl mx-auto py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold text-navy">
                Votre panier
              </h1>
              <p className="text-navy/60 mt-1">
                {itemCount} article{itemCount > 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={clearCart}
              className="flex items-center gap-2 text-red-500 hover:text-red-600 transition-colors"
              data-testid="clear-cart-button"
            >
              <Trash2 size={18} />
              Vider le panier
            </button>
          </div>

          {/* Cart Items */}
          <div className="space-y-4">
            {cart.items.map((item) => (
              <CartItemCard key={item.dish_id} item={item} />
            ))}
          </div>

          {/* Mode de réception */}
          <div className="bg-white rounded-2xl p-6 space-y-3" data-testid="delivery-mode-selector">
            <h3 className="font-heading text-lg font-semibold text-navy">Mode de réception</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => selectMode('livraison')}
                className={`rounded-xl border-2 p-4 text-left transition-colors ${
                  deliveryMode === 'livraison'
                    ? 'border-terracotta bg-terracotta/5'
                    : 'border-stone-200 hover:border-terracotta/40'
                }`}
                data-testid="mode-livraison-button"
              >
                <p className="font-semibold text-navy">Livraison</p>
                <p className="text-sm text-navy/60">à Genève — 4.00 CHF (offerte dès 80.–)</p>
              </button>
              <button
                onClick={() => selectMode('retrait')}
                className={`rounded-xl border-2 p-4 text-left transition-colors ${
                  deliveryMode === 'retrait'
                    ? 'border-terracotta bg-terracotta/5'
                    : 'border-stone-200 hover:border-terracotta/40'
                }`}
                data-testid="mode-retrait-button"
              >
                <p className="font-semibold text-navy">Retrait sur place</p>
                <p className="text-sm text-sage font-medium">Gratuit</p>
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-white rounded-2xl p-6 space-y-4">
            <div className="flex justify-between items-center text-lg">
              <span className="text-navy/70">Sous-total</span>
              <span className="font-semibold text-navy">
                {(cart.subtotal ?? cart.total)?.toFixed(2)} CHF
              </span>
            </div>
            {cart.discount > 0 && (
              <div className="space-y-1" data-testid="cart-formules-discount">
                <div className="flex justify-between items-center text-lg">
                  <span className="text-sage font-medium">Formules appliquées</span>
                  <span className="text-sage font-semibold">-{cart.discount.toFixed(2)} CHF</span>
                </div>
                {cart.formules?.map((f) => (
                  <p key={f.label} className="text-sm text-navy/50">
                    {f.count}x {f.label}
                  </p>
                ))}
              </div>
            )}
            <div className="flex justify-between items-center text-lg">
              <span className="text-navy/70">
                {deliveryMode === 'livraison' ? 'Livraison' : 'Retrait sur place'}
              </span>
              {deliveryMode === 'livraison' ? (
                freeDelivery ? (
                  <span className="text-sage font-medium" data-testid="free-delivery-label">Offerte (dès 80.–)</span>
                ) : (
                  <span className="font-semibold text-navy">4.00 CHF</span>
                )
              ) : (
                <span className="text-sage font-medium">Gratuit</span>
              )}
            </div>
            <hr className="border-stone-200" />
            <div className="flex justify-between items-center text-xl">
              <span className="font-semibold text-navy">Total</span>
              <span className="font-bold text-terracotta" data-testid="cart-grand-total">
                {grandTotal.toFixed(2)} CHF
              </span>
            </div>
            <p className="text-sm text-navy/60" data-testid="payment-terminal-note">
              Paiement par terminal (carte ou Twint) lors de la livraison ou du retrait.
            </p>

            <Link
              to="/checkout"
              className="w-full btn-primary flex items-center justify-center gap-2 mt-4"
              data-testid="checkout-button"
            >
              Passer la commande
              <ArrowRight size={20} />
            </Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
